// Build-time + runtime safe helpers for WebSocket URLs

/**
 * Returns the WS base from env if provided (inlined at build time by Next),
 * otherwise falls back to window origin (client-only).
 * Examples for env:
 *  - production: wss://townlit.com/ws
 *  - dev:        wss://api.dev.townlit.com/ws
 * You can also provide origin-only, e.g. wss://townlit.com, the builder handles /ws.
 */
export function getWsBase(): string {
  // NOTE: process.env.* is inlined by Next during build; do not read it in runtime (console).
  const env = (process.env.NEXT_PUBLIC_WS_URL || "").trim().replace(/\/+$/, "");
  if (env) return env; // e.g. "wss://townlit.com/ws" or "ws://api.dev.townlit.com"

  // Fallback for client-side only (SSR-safe)
  if (typeof window !== "undefined") {
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${window.location.host}`;
  }
  return "";
}

/**
 * Builds a full WS URL under /ws, avoiding double "/ws" if the base already ends with it.
 * @param pathAfterWs path segment placed after /ws (e.g., "conversation/" or "conversation/123/")
 * @param qs          query string key-value pairs
 */
export function buildWsUrl(pathAfterWs: string, qs: Record<string, string> = {}) {
  const base = getWsBase();                 // e.g., "wss://townlit.com/ws" or "ws://api.dev.townlit.com"
  const endsWithWs = /\/ws$/i.test(base);   // true if base already ends with "/ws"
  const prefix = endsWithWs ? "" : "/ws";   // add "/ws" only when needed

  const query = Object.entries(qs)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

  // Join parts and collapse duplicate slashes (excluding the scheme part)
  const url = `${base}${prefix}/${pathAfterWs}${query ? "?" + query : ""}`;
  return url.replace(/([^:]\/)\/+/g, "$1");
}
